import { addDays, formatPlainDate, type PlainDate } from "@kedaat/engine";
import type { CalendarModule } from "./zmanim";

export interface VerifyDate {
  date: PlainDate;
  /** Why this date is in the sheet, e.g. "היום הקצר ביותר". */
  reason?: string;
}

/** The solstice within a month, found by comparing day lengths. */
function extremeDay(
  year: number,
  month: number,
  dayLength: (date: PlainDate) => number | null,
  pick: "shortest" | "longest",
): PlainDate | null {
  let best: { date: PlainDate; length: number } | null = null;
  for (let day = 15; day <= 27; day++) {
    const date = { year, month, day };
    const length = dayLength(date);
    if (length === null) continue;
    const better =
      best === null || (pick === "shortest" ? length < best.length : length > best.length);
    if (better) best = { date, length };
  }
  return best?.date ?? null;
}

/**
 * The dates a rav checks the profile on (SPEC 10.3): the middle of every month, the two
 * solstices, and Erev Pesach, which is the only day the chametz zmanim are shown.
 */
export function verifyDates(
  year: number,
  calendar: CalendarModule,
  dayLength: (date: PlainDate) => number | null,
): VerifyDate[] {
  const dates: VerifyDate[] = Array.from({ length: 12 }, (_, i) => ({
    date: { year, month: i + 1, day: 15 },
  }));

  const shortest = extremeDay(year, 12, dayLength, "shortest");
  const longest = extremeDay(year, 6, dayLength, "longest");
  if (shortest) dates.push({ date: shortest, reason: "היום הקצר ביותר" });
  if (longest) dates.push({ date: longest, reason: "היום הארוך ביותר" });

  // Erev Pesach falls in March or April; scan those two months for it.
  for (let i = 0; i < 62; i++) {
    const date = addDays({ year, month: 3, day: 1 }, i);
    if (calendar.hebrewDayInfo(date).isErevPesach) {
      dates.push({ date, reason: "ערב פסח" });
      break;
    }
  }

  const seen = new Set<string>();
  return dates
    .sort((a, b) => formatPlainDate(a.date).localeCompare(formatPlainDate(b.date)))
    .filter((d) => {
      const iso = formatPlainDate(d.date);
      if (seen.has(iso)) return false;
      seen.add(iso);
      return true;
    });
}
